import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import Swal from "sweetalert2";
import "./AdminManagement.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminManagement() {
  const { isAdmin, userInfo, authFetch } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchFilters, setSearchFilters] = useState({
    searchTerm: '',
    roleFilter: 'all'
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    role: "warehouse",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!isAdmin()) {
      navigate("/admin/dashboard");
      return;
    }

    fetchAdminUsers();
  }, [isAdmin, navigate]);

  const fetchAdminUsers = async () => {
    try {
      setLoading(true);
      const response = await authFetch(
        `${API_BASE_URL}/Users/admin-users`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch admin users");
      }
      const data = await response.json();
      setAdminUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch admin users. Please try again.",
        icon: "error",
        confirmButtonColor: "#45a049",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const handleSearchFilterChange = (e) => {
    const { name, value } = e.target;
    const newFilters = {
      ...searchFilters,
      [name]: value
    };
    setSearchFilters(newFilters);
    applyFilters(newFilters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      searchTerm: '',
      roleFilter: 'all'
    };
    setSearchFilters(resetFilters);
    setFilteredUsers(adminUsers);
  };

  const applyFilters = (filters) => {
    const { searchTerm, roleFilter } = filters;

    let filtered = [...adminUsers];
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        (user.username && user.username.toLowerCase().includes(searchLower)) ||
        (user.email && user.email.toLowerCase().includes(searchLower)) ||
        (`${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(searchLower))
      );
    }
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  // Mirrors PasswordValidator.ValidateAdminPassword on the server. Every role created
  // here (admin / warehouse / sales) counts as an admin account, so the strict rules
  // always apply. Checked client side so the form fails fast instead of only on submit.
  const passwordRules = [
    { key: "length", label: "At least 8 characters", test: (p) => p.length >= 8 },
    { key: "upper", label: "One uppercase letter (A-Z)", test: (p) => /[A-Z]/.test(p) },
    { key: "number", label: "One number (0-9)", test: (p) => /[0-9]/.test(p) },
    { key: "special", label: "One special character", test: (p) => /[^a-zA-Z0-9]/.test(p) },
  ];

  const failedPasswordRules = (password) =>
    passwordRules.filter((r) => !r.test(password || ""));

  const validateForm = (isEdit = false) => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = "Username is required";
    } else if (formData.username.length < 4) {
      errors.username = "Username must be at least 4 characters";
    }

    if (!isEdit && !formData.password.trim()) {
      errors.password = "Password is required";
    } else if (formData.password.trim() && failedPasswordRules(formData.password).length > 0) {
      // On edit a blank password means "keep the current one", so only check what was typed
      errors.password = "Password does not meet the requirements below.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateForm()) {
      return;
    }

    try {
      const response = await authFetch(
        `${API_BASE_URL}/Users/register-admin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Username: formData.username,
            Password: formData.password,
            Email: formData.email,
            FirstName: formData.firstName,
            LastName: formData.lastName,
            PhoneNumber: formData.phoneNumber,
            Role: formData.role,
            CreatorId: userInfo.userId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create admin user");
      }

      setFormData({
        username: "",
        password: "",
        email: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        role: "warehouse",
      });
      setShowAddModal(false);

      Swal.fire({
        title: "Success!",
        text: "Admin user created successfully!",
        icon: "success",
        timer: 1500,
        confirmButtonColor: "#45a049",
      });

      fetchAdminUsers();
    } catch (error) {
      setSubmitError(
        error.message || "An error occurred during admin creation"
      );
      Swal.fire({
        title: "Error!",
        text: error.message || "An error occurred during admin creation",
        icon: "error",
        confirmButtonColor: "#45a049",
      });
    }
  };

  const handleEdit = (user) => {
    const updatedFormData = {
      username: user.username,
      password: "",
      email: user.email,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      phoneNumber: user.phoneNumber || "",

      role: user.role === "inactive" ? "warehouse" : user.role,
      userID: user.userId,
    };

    setCurrentUser(user);
    setFormData(updatedFormData);
    setShowEditModal(true);
  };

  const handleDelete = (user) => {
    setCurrentUser(user);
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to disable the admin user ${user.username}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e74c3c",
      cancelButtonColor: "#7a7a7a",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteConfirm(user);
      }
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setFormErrors({});
    setSubmitError("");
    setCurrentUser(null);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateForm(true)) {
      return;
    }

    try {
      const response = await authFetch(
        `${API_BASE_URL}/Users/update-admin/${formData.userID}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            UserID: formData.userID,
            Username: formData.username,
            Password: formData.password.trim() ? formData.password : null,
            Email: formData.email,
            FirstName: formData.firstName,
            LastName: formData.lastName,
            PhoneNumber: formData.phoneNumber,
            Role: formData.role,
            UpdatedBy: userInfo.userId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update admin user");
      }

      setShowEditModal(false);
      Swal.fire({
        title: "Success!",
        text: "Admin user updated successfully!",
        icon: "success",
        timer: 1500,
        confirmButtonColor: "#45a049",
      });

      fetchAdminUsers();
    } catch (error) {
      setSubmitError(error.message || "An error occurred during admin update");
      Swal.fire({
        title: "Error!",
        text: error.message || "An error occurred during admin update",
        icon: "error",
        confirmButtonColor: "#45a049",
      });
    }
  };

  const handleDeleteConfirm = async (user) => {
    try {
      const userToDelete = user || currentUser;

      const response = await authFetch(
        `${API_BASE_URL}/Users/delete-admin/${userToDelete.userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            UserID: userToDelete.userId,
            UpdatedBy: userInfo.userId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to disable admin user");
      }

      Swal.fire({
        title: "Disabled!",
        text: "Admin user has been disabled successfully.",
        icon: "success",
        timer: 1500,
        confirmButtonColor: "#45a049",
      });

      fetchAdminUsers();
    } catch (error) {
      setSubmitError(
        error.message || "An error occurred during admin disabling"
      );
      Swal.fire({
        title: "Error!",
        text: error.message || "An error occurred during admin disabling",
        icon: "error",
        confirmButtonColor: "#45a049",
      });
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "admin":
        return "admin-role-badge admin-role-badge-full";
      case "warehouse":
        return "admin-role-badge admin-role-badge-warehouse";
      case "sales":
        return "admin-role-badge admin-role-badge-sales";
      case 'inactive':
        return 'admin-role-badge admin-role-badge-inactive';
      default:
        return "admin-role-badge";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "admin":
        return "Full Admin";
      case "warehouse":
        return "Warehouse Admin";
      case "sales":
        return "Sales Admin";
      case "inactive":
        return "Inactive";
      default:
        return role;
    }
  };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear(); 

            return `${day}/${month}/${year}`; 
        } catch (e) {
            return dateString;
        }
    };

  return (
    <div className="admin-management-container">
      {/* Back to Dashboard */}
      <div className="admin-management-header">
        <button
          className="admin-management-back-btn"
          onClick={() => navigate("/admin/dashboard")}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      <div className="admin-management-title">
        <h1>Admin User Management</h1>
      </div>

      <div className="admin-management-actions-top">
        <button
          className="admin-management-add-btn"
          onClick={() => {
            setFormData({
              username: "",
              password: "",
              email: "",
              firstName: "",
              lastName: "",
              phoneNumber: "",
              role: "warehouse",
            });
            setFormErrors({});
            setShowAddModal(true);
          }}
        >
          <i className="fas fa-user-plus"></i> Add New Admin
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="admin-management-search-filter">
        <div className="admin-management-search-bar">
          <label htmlFor="searchTerm">Search</label>
          <input
            type="text"
            id="searchTerm"
            name="searchTerm"
            placeholder="Search by username, email, or name..."
            value={searchFilters.searchTerm}
            onChange={handleSearchFilterChange}
            className="admin-management-search-input"
          />
        </div>

        <div className="admin-management-filter">
          <label htmlFor="roleFilter">Role</label>
          <select
            id="roleFilter"
            name="roleFilter"
            value={searchFilters.roleFilter}
            onChange={handleSearchFilterChange}
            className="admin-management-filter-select"
          >
            <option value="all">All Roles</option>
            <option value="admin">Full Admin</option>
            <option value="warehouse">Warehouse Admin</option>
            <option value="sales">Sales Admin</option>
          </select>
        </div>

        <button
          className="admin-management-filter-btn"
          onClick={handleResetFilters}
        >
          Search
        </button>
      </div>

      {/* Table Section */}
      {loading ? (
        <div className="admin-management-loading">
          <div className="admin-management-spinner"></div>
          <p>Loading admin users...</p>
        </div>
      ) : (
        <div className="admin-management-table-container">
          <table className="admin-management-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="admin-management-no-data">
                    {adminUsers.length === 0 ? "No admin users found. Add your first admin user!" : "No users match your search criteria."}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.userID}>
                    <td>{user.username}</td>
                    <td>
                      {user.firstName} {user.lastName}
                    </td>
                    <td>{user.email || "N/A"}</td>
                    <td>{user.phoneNumber || "N/A"}</td>
                    <td>
                      <span className={getRoleBadgeClass(user.role)}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td className="admin-management-actions">
                      <button
                        className="admin-management-action-btn admin-management-edit-btn"
                        title="Edit User"
                        onClick={() => handleEdit(user)}
                      >
                        <i className="fas fa-edit"></i>
                        <span className="admin-action-text">Edit</span>
                      </button>
                      {userInfo && user && String(userInfo.userId) !== String(user.userId) && (
                        <button
                          className="admin-management-action-btn admin-management-disable-btn"
                          title="Delete User"
                          onClick={() => handleDelete(user)}
                          disabled={user.role === 'inactive'}
                        >
                          <i className="fas fa-user-slash"></i>
                          <span className="admin-action-text">Remove</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Admin Modal Section */}
      {showAddModal && (
        <div className="admin-management-modal-overlay">
          <div className="admin-management-modal">
            <div className="admin-management-modal-header">
              <h2>Add New Admin User</h2>
              <button
                className="admin-management-modal-close"
                onClick={() => setShowAddModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-management-form">
              <div className="admin-management-form-row">
                <div className="admin-management-form-group">
                  <label htmlFor="username">
                    Username <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className={
                      formErrors.username
                        ? "admin-management-input error"
                        : "admin-management-input"
                    }
                  />
                  {formErrors.username && (
                    <div className="admin-management-error">
                      {formErrors.username}
                    </div>
                  )}
                </div>

                <div className="admin-management-form-group">
                  <label htmlFor="role">
                    Role <span className="required">*</span>
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="admin-management-input"
                  >
                    <option value="warehouse">Warehouse Admin</option>
                    <option value="sales">Sales Admin</option>
                    <option value="admin">Full Admin</option>
                  </select>
                </div>
              </div>

              <div className="admin-management-form-row">
                <div className="admin-management-form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="admin-management-input"
                  />
                </div>

                <div className="admin-management-form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="admin-management-input"
                  />
                </div>
              </div>

              <div className="admin-management-form-row">
                <div className="admin-management-form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <input
                    type="text"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="admin-management-input"
                  />
                </div>

                <div className="admin-management-form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={
                      formErrors.email
                        ? "admin-management-input error"
                        : "admin-management-input"
                    }
                  />
                  {formErrors.email && (
                    <div className="admin-management-error">
                      {formErrors.email}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-management-form-group">
                <label htmlFor="password">
                  Password <span className="required">*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={
                    formErrors.password
                      ? "admin-management-input error"
                      : "admin-management-input"
                  }
                />
                {formErrors.password && (
                  <div className="admin-management-error">
                    {formErrors.password}
                  </div>
                )}
                <ul className="admin-management-password-rules">
                  {passwordRules.map((rule) => {
                    const met = rule.test(formData.password || "");
                    return (
                      <li key={rule.key} className={met ? "is-met" : ""}>
                        <i className={met ? "fas fa-check-circle" : "far fa-circle"}></i>
                        {rule.label}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {submitError && (
                <div className="admin-management-submit-error">
                  <i className="fas fa-exclamation-circle"></i> {submitError}
                </div>
              )}

              <div className="admin-management-form-actions">
                <button
                  type="button"
                  className="admin-management-btn-cancel"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-management-btn-submit">
                  Create Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Admin Modal Section */}
      {showEditModal && (
        <div className="admin-management-modal-overlay">
          <div className="admin-management-modal">
            <div className="admin-management-modal-header">
              <h2>Edit Admin User</h2>
              <button
                className="admin-management-modal-close"
                onClick={handleCloseEditModal}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form
              onSubmit={handleUpdateSubmit}
              className="admin-management-form"
            >
              <div className="admin-management-form-row">
                <div className="admin-management-form-group">
                  <label htmlFor="username">
                    Username <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className={
                      formErrors.username
                        ? "admin-management-input error"
                        : "admin-management-input"
                    }
                  />
                  {formErrors.username && (
                    <div className="admin-management-error">
                      {formErrors.username}
                    </div>
                  )}
                </div>

                <div className="admin-management-form-group">
                  <label htmlFor="role">
                    Role <span className="required">*</span>
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="admin-management-input"
                  >
                    <option value="warehouse">Warehouse Admin</option>
                    <option value="sales">Sales Admin</option>
                    <option value="admin">Full Admin</option>
                  </select>
                </div>

              </div>

              <div className="admin-management-form-row">
                <div className="admin-management-form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="admin-management-input"
                  />
                </div>

                <div className="admin-management-form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="admin-management-input"
                  />
                </div>
              </div>

              <div className="admin-management-form-row">
                <div className="admin-management-form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <input
                    type="text"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="admin-management-input"
                  />
                </div>

                <div className="admin-management-form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={
                      formErrors.email
                        ? "admin-management-input error"
                        : "admin-management-input"
                    }
                  />
                  {formErrors.email && (
                    <div className="admin-management-error">
                      {formErrors.email}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-management-form-group">
                <label htmlFor="password">
                  New Password (leave blank to keep unchanged)
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={
                    formErrors.password
                      ? "admin-management-input error"
                      : "admin-management-input"
                  }
                />
                {formErrors.password && (
                  <div className="admin-management-error">
                    {formErrors.password}
                  </div>
                )}
                <ul className="admin-management-password-rules">
                  {passwordRules.map((rule) => {
                    const met = rule.test(formData.password || "");
                    return (
                      <li key={rule.key} className={met ? "is-met" : ""}>
                        <i className={met ? "fas fa-check-circle" : "far fa-circle"}></i>
                        {rule.label}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {submitError && (
                <div className="admin-management-submit-error">
                  <i className="fas fa-exclamation-circle"></i> {submitError}
                </div>
              )}

              <div className="admin-management-form-actions">
                <button
                  type="button"
                  className="admin-management-btn-cancel"
                  onClick={handleCloseEditModal}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-management-btn-submit">
                  Update Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminManagement;